import React, { useMemo, useState } from 'react';
import { Box, Chip, CircularProgress, Link, Tab, Tabs, Typography } from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import { PullRequestDetails } from '@finvari/deliver';
import { useTestExecutions } from '../../contexts/TestExecutionsContext';
import { computePrWorkflow } from '../../workflow/prWorkflow';
import { GITHUB_REPO_URL, NODE, TEST_LANES, WORKFLOW_NODES_BY_ID } from '../../workflow/workflowConfig';
import { laneStatusGlyph, unitStatusGlyph } from './panel/statusIcons';
import OverviewTab from './panel/OverviewTab';
import FilesTab from './panel/FilesTab';
import ReviewTab from './panel/ReviewTab';
import UnitTestsTab from './panel/UnitTestsTab';
import TestLaneTab from './panel/TestLaneTab';
import EnvironmentsTab from './panel/EnvironmentsTab';
import WorkflowHelp from './panel/WorkflowHelp';

interface PullRequestPanelProps {
  prNumber: number | null;
  pr: PullRequestDetails | undefined;
  openPrs: PullRequestDetails[];
  // True while the PR lists are still being fetched, so a deep link to a PR we haven't received yet
  // waits rather than claiming the PR doesn't exist.
  loading: boolean;
  onSelectPr: (prNumber: number) => void;
  // The selected stage, used to explain that stage (or the page) when no PR is selected.
  stage: string | null;
  // Selects a stage from an in-app stage link in the workflow help (threaded down to WorkflowHelp).
  onSelectStage?: (stage: string) => void;
}

const GITHUB_PR_BASE = GITHUB_REPO_URL + '/pull/';

// The fixed tabs. A scoped test lane's tab is identified by its lane id, so a tab id is a string
// rather than a position — which is what lets a stage selection name the tab it should open without
// anyone having to keep index arithmetic in step with TEST_LANES.
const TAB = {
  overview: 'overview',
  files: 'files',
  review: 'review',
  unit: 'unit',
  environments: 'environments',
} as const;

// The review stages whose content the Review tab shows. 'open' is deliberately absent: it means no
// one other than the author has engaged yet, so the Review tab would be empty. There is no approved
// stage (approval is shown by absence), and 'merge-conflicts' is a mechanical blocker with nothing to
// show on the Review tab, so both fall through to Overview.
const REVIEW_STAGES: string[] = [NODE.reviewing, NODE.changesRequested];

// Which tab a PR opens on, given the stage the user came in from: a stage that is about one part of
// the PR opens the tab showing that part, so clicking a PR under "Recompute testing" lands on its
// Recompute Test tab. Stages with no obvious home (parked, Open, Ready, Merged) open Overview.
export function tabForStage(stage: string | null): string {
  if (!stage) return TAB.overview;
  const node = WORKFLOW_NODES_BY_ID[stage];
  if (!node) return TAB.overview;
  if (node.laneId) return node.laneId;
  if (node.kind === 'unit') return TAB.unit;
  if (REVIEW_STAGES.includes(stage)) return TAB.review;
  return TAB.overview;
}

const CenteredMessage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      p: 4,
      textAlign: 'center',
    }}
  >
    <Typography variant="body1" color="text.secondary">{children}</Typography>
  </Box>
);

const PullRequestPanel: React.FC<PullRequestPanelProps> = ({ prNumber, pr, openPrs, loading, onSelectPr, stage, onSelectStage }) => {
  const { executionsByPr } = useTestExecutions();
  const stageTab = tabForStage(stage);
  const [activeTab, setActiveTab] = useState(stageTab);

  const workflow = useMemo(() => {
    if (!pr) return null;
    const execs = executionsByPr.get(pr.pr.number) ?? [];
    return computePrWorkflow(pr, execs);
  }, [pr, executionsByPr]);

  // Whenever the selected PR or stage changes, jump back to the stage's tab so we don't linger on,
  // say, a lane tab that isn't meaningful for the new PR.
  const prKey = pr?.pr.number ?? null;
  const selectionKey = prKey + '|' + stage;
  const [lastSelectionKey, setLastSelectionKey] = useState(selectionKey);
  if (selectionKey !== lastSelectionKey) {
    setLastSelectionKey(selectionKey);
    setActiveTab(stageTab);
  }

  if (prNumber === null) {
    // No PR selected: the panel explains the selected stage, or gives page help when nothing is.
    // Flex so WorkflowHelp can fill the panel (hint top-left, reading column centered in the rest).
    return (
      <Box sx={{ height: '100%', overflow: 'auto', p: 2, minHeight: 0, display: 'flex' }}>
        <WorkflowHelp stage={stage} onSelectStage={onSelectStage} />
      </Box>
    );
  }
  if ((!pr || !workflow) && loading) {
    // A deep link like ?pr=2714 selects the PR before any PR data has arrived, so until the fetch
    // finishes we can't tell "not in our window" from "not fetched yet" — show progress, not a
    // verdict.
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (!pr || !workflow) {
    return (
      <CenteredMessage>
        PR #{prNumber} isn&apos;t loaded (closed, or merged more than a week ago).{' '}
        <Link href={`${GITHUB_PR_BASE}${prNumber}`} target="_blank" rel="noopener noreferrer">
          View on GitHub
        </Link>
      </CenteredMessage>
    );
  }

  const gh = pr.pr;

  // Tab set: fixed tabs plus one per scoped test lane, each carrying a status glyph.
  const tabDefs: Array<{ id: string; label: string; glyph?: React.ReactNode }> = [
    { id: TAB.overview, label: 'Overview' },
    { id: TAB.files, label: 'Files' },
    { id: TAB.review, label: 'Review' },
    { id: TAB.unit, label: 'Unit Tests', glyph: unitStatusGlyph(workflow.unit, 15) },
    ...TEST_LANES.map(lane => ({
      id: lane.id,
      label: lane.label,
      glyph: laneStatusGlyph(workflow.lanes[lane.id], 15),
    })),
    { id: TAB.environments, label: 'Envs' },
  ];

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* Header */}
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        {/* Title, author, branches, labels and the GitHub link share one wrapping row: the panel is
            short on vertical space, and on a wide window this metadata fits beside the title. */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1.5, rowGap: 1 }}>
          <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
            {gh.title}{' '}
            <Typography component="span" variant="h6" color="text.secondary">#{gh.number}</Typography>
          </Typography>
          <Typography variant="body2" color="text.secondary">by {gh.user?.login ?? 'Unknown'}</Typography>
          <Chip size="small" variant="outlined" label={`${gh.head?.ref} → ${gh.base?.ref}`} />
          {workflow.parked && <Chip size="small" color="default" label={workflow.parked} />}
          {workflow.priority && <Chip size="small" color="error" label="priority" />}
          {(gh.labels ?? []).map(l => (
            <Chip key={l.name} size="small" variant="outlined" label={l.name} />
          ))}
          <Link
            href={gh.html_url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap', ml: 'auto' }}
          >
            GitHub <OpenInNew fontSize="small" />
          </Link>
        </Box>
      </Box>

      {/* flexShrink: 0 is load-bearing, not tidiness. minHeight overrides the flex default of
          min-height:auto, so without it a tall tab body (Overview, Unit Tests) makes this column
          overflow and squeezes the strip to exactly 40px — 39px of content once the bottom border
          is taken out of the border box. The 40px-high tabs then hang 1px below the scroller, and
          MUI decides a tab is off-screen at 1% clipping in ANY direction, so a 1px vertical crop
          turns on the horizontal scroll arrows on a window with room to spare. */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 40, flexShrink: 0 }}
      >
        {tabDefs.map(t => (
          <Tab
            key={t.id}
            sx={{ minHeight: 40, py: 0.5 }}
            iconPosition="end"
            icon={t.glyph ? <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5 }}>{t.glyph}</Box> : undefined}
            label={t.label}
            value={t.id}
          />
        ))}
      </Tabs>

      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2, minHeight: 0 }}>
        {activeTab === TAB.overview && (
          <OverviewTab pr={pr} workflow={workflow} openPrs={openPrs} onSelectPr={onSelectPr} />
        )}
        {activeTab === TAB.files && <FilesTab pr={pr} />}
        {activeTab === TAB.review && <ReviewTab pr={pr} />}
        {activeTab === TAB.unit && <UnitTestsTab pr={pr} status={workflow.unit} />}
        {TEST_LANES.map(lane =>
          activeTab === lane.id ? (
            <TestLaneTab key={lane.id} pr={pr} lane={lane} status={workflow.lanes[lane.id]} />
          ) : null
        )}
        {activeTab === TAB.environments && <EnvironmentsTab pr={pr} />}
      </Box>
    </Box>
  );
};

export default PullRequestPanel;
